import {
  getAllLegalDocuments,
  getLegalDocumentById,
  getLegalDocumentsByType,
  postCreateLegalDocument,
  putActivateLegalDocument,
  putDeactivateLegalDocument,
  putUpdateLegalDocument,
  TLegalDocumentListFilters,
} from '@/services/api/requests/legal';
import { usePagination } from '../usePagination';
import { TLegalDocument, TLegalDocumentType } from '@/types/Legal.types';
import useToast from '../useToast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TCustomAxiosError } from '@/services/api';
import queryKeys from '@/constants/queryKeys';

export const useLegalDocuments = (filterParams?: TLegalDocumentListFilters) => {
  return usePagination<TLegalDocument[], TLegalDocumentListFilters>({
    query: getAllLegalDocuments,
    queryKey: queryKeys.legalDocument.allLegalDocuments,
    queryParams: filterParams,
  });
};

export const useLegalDocumentsByType = (type?: TLegalDocumentType) => {
  return useQuery({
    queryKey: [queryKeys.legalDocument.allLegalDocuments, 'type', type],
    queryFn: async () => {
      if (!type) return [];
      return getLegalDocumentsByType(type);
    },
    enabled: Boolean(type),
  });
};

export const useCreateLegalDocument = (onSuccessCallback?: (document: TLegalDocument) => void) => {
  const toast = useToast();

  const { isPending, data, mutate } = useMutation({
    mutationFn: async ({ type }: { type: string }) => {
      const data = await postCreateLegalDocument({ type });
      return data;
    },
    onSuccess: (document) => {
      if (onSuccessCallback) {
        onSuccessCallback(document);
      }
    },
    onError: (error: TCustomAxiosError) => {
      toast({
        status: 'error',
        title: 'Creating legal document error',
        description: error?.errors[0]?.message,
      });
    },
  });

  return { isPending, data, mutate };
};

export const useLegalDocumentById = (
  id: string,
): { data: TLegalDocument | undefined; isLoading: boolean; isError: boolean; isSuccess: boolean } => {
  const { isLoading, data, isError, isSuccess } = useQuery({
    queryKey: [queryKeys.legalDocument.allLegalDocuments, id],
    queryFn: async () => {
      const document = await getLegalDocumentById(id);

      return document;
    },
  });

  return { isLoading, data, isError, isSuccess };
};

export const useUpdateLegalDocument = (onSuccessCallback?: () => void) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { isPending, mutate, isError } = useMutation({
    mutationFn: async (doc: {
      id: string;
      content: string;
      type: string;
      version: string;
      s3Key: string;
      isActive: boolean;
    }) => {
      const data = await putUpdateLegalDocument(doc);
      return data;
    },
    onSuccess: (document) => {
      // Refresh the list and the open document detail (both share the
      // 'all-legal-documents' query-key prefix) instead of reloading the page.
      queryClient.setQueryData([queryKeys.legalDocument.allLegalDocuments, document._id], document);
      queryClient.invalidateQueries({ queryKey: [queryKeys.legalDocument.allLegalDocuments] });
      onSuccessCallback?.();
    },
    onError: (error: TCustomAxiosError) => {
      toast({
        status: 'error',
        title: 'Updating legal document error',
        description: error?.errors[0]?.message,
      });
    },
  });

  return { isPending, mutate, isError };
};

export const useToggleActivateLegalDocument = (onSuccessCallback?: () => void) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { isPending, mutate, isError } = useMutation({
    mutationFn: async (doc: { isActive: boolean; type: string; version: string }) => {
      const data = doc.isActive ? putDeactivateLegalDocument(doc) : putActivateLegalDocument(doc);
      return data;
    },
    onSuccess: (document) => {
      queryClient.setQueryData([queryKeys.legalDocument.allLegalDocuments, document._id], document);
      queryClient.invalidateQueries({ queryKey: [queryKeys.legalDocument.allLegalDocuments] });
      onSuccessCallback?.();
    },
    onError: (error: TCustomAxiosError) => {
      toast({
        status: 'error',
        title: 'Toggle activate legal document error',
        description: error?.errors[0]?.message,
      });
    },
  });

  return { isPending, mutate, isError };
};
